import type { Playlist, Track, ViewSelection } from "../model";
import { splitArtists } from "../services/artists";

function text(track: Track) {
  return [track.title, track.artist, track.album, track.label, ...track.genres, ...track.tags, ...track.categories, track.year, track.bpm, track.musicalKey].join(" ").toLocaleLowerCase();
}

function matchesRule(track: Track, field: Playlist["rules"][number]["field"], operator: Playlist["rules"][number]["operator"], rawValue: string | number | boolean) {
  const value = field === "key" ? track.musicalKey : field === "genre" ? track.genres : track[field];
  const haystack = Array.isArray(value) ? value.join(" ").toLocaleLowerCase() : String(value ?? "").toLocaleLowerCase();
  const needle = String(rawValue).toLocaleLowerCase();
  if (operator === "is") return haystack === needle;
  if (operator === "isNot") return haystack !== needle;
  if (operator === "contains") return haystack.includes(needle);
  if (operator === "greaterThan") return Number(value) > Number(rawValue);
  return Number(value) < Number(rawValue);
}

export function playlistTracks(tracks: Track[], playlist: Playlist) {
  if (playlist.type === "manual") return tracks.filter((track) => playlist.trackIds.includes(track.id));
  return tracks.filter((track) => {
    const results = playlist.rules.map((rule) => matchesRule(track, rule.field, rule.operator, rule.value));
    return playlist.match === "all" ? results.every(Boolean) : results.some(Boolean);
  });
}

function inView(track: Track, view: ViewSelection, playlists: Playlist[], hashes: Map<string, number>) {
  if (view.view === "all") return true;
  if (view.view === "favorites") return track.favorite;
  if (view.view === "recent") return Date.now() - new Date(track.addedAt).getTime() < 30 * 24 * 60 * 60 * 1000;
  if (view.view === "missing") return !track.artist || !track.album || !track.year || track.genres.length === 0 || !track.label;
  if (view.view === "duplicates") return (hashes.get(track.fileHash) ?? 0) > 1;
  if (view.view === "artist") return splitArtists(track.artist).includes(view.value ?? "");
  if (view.view === "album") return track.album === view.value;
  if (view.view === "genre") return track.genres.includes(view.value ?? "");
  if (view.view === "label") return track.label === view.value;
  if (view.view === "category") return track.categories.includes(view.value ?? "");
  const playlist = playlists.find((item) => item.id === view.value);
  if (!playlist) return false;
  return playlistTracks([track], playlist).length === 1;
}

export function visibleTracks(tracks: Track[], playlists: Playlist[], view: ViewSelection, query: string, genre: string, label: string, sortKey: keyof Track, direction: "asc" | "desc") {
  const hashes = new Map<string, number>();
  for (const track of tracks) hashes.set(track.fileHash, (hashes.get(track.fileHash) ?? 0) + 1);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  return tracks
    .filter((track) => inView(track, view, playlists, hashes))
    .filter((track) => !normalizedQuery || text(track).includes(normalizedQuery))
    .filter((track) => !genre || track.genres.includes(genre))
    .filter((track) => !label || track.label === label)
    .sort((left, right) => {
      const a = left[sortKey];
      const b = right[sortKey];
      const result = typeof a === "number" && typeof b === "number"
        ? a - b
        : String(Array.isArray(a) ? a.join(", ") : a ?? "").localeCompare(String(Array.isArray(b) ? b.join(", ") : b ?? ""), undefined, { numeric: true, sensitivity: "base" });
      return direction === "asc" ? result : -result;
    });
}
